import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileDown, Sparkles } from "lucide-react";
import { useFormatCurrency, useCurrency } from "@/lib/CurrencyContext";
import {
  totalProjectCost, estimatedProfit, estimatedROI, margin,
} from "@/lib/calculations";
import { generateProjectReport, classifyFeasibility } from "@/lib/projectReport";
import { toast } from "sonner";

const TONE_CLASSES = {
  green: "bg-sage-100 border-sage-200 text-sage-700",
  amber: "bg-amber-50 border-amber-200 text-amber-700",
  red:   "bg-red-50 border-red-200 text-red-700",
};

// Open=controlled by parent. onChange(patch) → persists field changes to project
// (so timeline_months / suggested_offer_price stick across reloads).
export default function ProjectReportDialog({ open, onOpenChange, project, onChange }) {
  const fmt = useFormatCurrency();
  const currency = useCurrency();

  // Local state mirrors the persisted project values; on Generate we flush.
  const [offer, setOffer] = useState(0);
  const [timeline, setTimeline] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !project) return;
    const persistedOffer = Number(project.suggested_offer_price) || 0;
    setOffer(persistedOffer > 0 ? persistedOffer : Number(project.purchase_price) || 0);
    setTimeline(Number(project.timeline_months) || 0);
  }, [open, project]);

  const liveTotals = useMemo(() => {
    if (!project) return null;
    const total = totalProjectCost(project);
    const purchase = Number(project.purchase_price) || 0;
    const arv = Number(project.target_sale_price) || 0;
    const profit = estimatedProfit(project);
    const roi = estimatedROI(project);
    const m = margin(project);

    // What changes if the user pitches `offer` instead of current purchase?
    const totalAtOffer = total - purchase + (Number(offer) || 0);
    const profitAtOffer = arv - totalAtOffer;
    const roiAtOffer = totalAtOffer > 0 ? profitAtOffer / totalAtOffer : 0;

    return { total, profit, roi, m, totalAtOffer, profitAtOffer, roiAtOffer };
  }, [project, offer]);

  const baseFeas = useMemo(() => liveTotals && classifyFeasibility({ roi: liveTotals.roi }), [liveTotals]);
  const offerFeas = useMemo(() => liveTotals && classifyFeasibility({ roi: liveTotals.roiAtOffer }), [liveTotals]);

  if (!project) return null;

  const persistAndGenerate = async () => {
    setBusy(true);
    try {
      // Persist edits to the project so the next open shows the same numbers.
      const patch = {};
      if ((Number(project.suggested_offer_price) || 0) !== Number(offer)) patch.suggested_offer_price = Number(offer) || 0;
      if ((Number(project.timeline_months) || 0) !== Number(timeline)) patch.timeline_months = Number(timeline) || 0;
      if (Object.keys(patch).length > 0 && onChange) onChange(patch);

      await generateProjectReport({
        project,
        currency,
        suggestedOffer: Number(offer) || 0,
        timeline: Number(timeline) || 0,
      });
      toast.success("Report generated");
      onOpenChange(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not generate the report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-white border-black/[0.08] text-[#27272A] sm:max-w-[640px] max-h-[90vh] overflow-y-auto"
        data-testid="report-dialog"
      >
        <DialogHeader>
          <DialogTitle className="fc-heading text-[#1C1C1C] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sage-700" /> Project Summary Report
          </DialogTitle>
          <DialogDescription className="text-xs text-[#78716C]">
            Generate a clean, share-ready PDF summarising the deal — including cost
            breakdown, ARV comparison, feasibility, and your offer justification.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Editable inputs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="fc-label mb-1.5">Suggested offer ({currency})</div>
              <Input
                data-testid="report-offer-input"
                type="number"
                value={offer}
                onChange={(e) => setOffer(Number(e.target.value) || 0)}
                placeholder="600000"
                className="fc-input h-10 rounded-md text-sm"
              />
              <div className="text-[11px] text-[#A8A29E] mt-1">
                Drives the "Offer Justification" line on the report.
              </div>
            </div>
            <div>
              <div className="fc-label mb-1.5">Timeline (months) — optional</div>
              <Input
                data-testid="report-timeline-input"
                type="number"
                value={timeline}
                onChange={(e) => setTimeline(Number(e.target.value) || 0)}
                placeholder="6"
                className="fc-input h-10 rounded-md text-sm"
              />
              <div className="text-[11px] text-[#A8A29E] mt-1">
                Appears as a pill on the report header.
              </div>
            </div>
          </div>

          {/* Live preview row */}
          {liveTotals && baseFeas && offerFeas && (
            <div className="rounded-lg border border-black/[0.08] bg-[#F5F5F4] p-4 space-y-3" data-testid="report-preview">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="fc-label">Current deal feasibility</div>
                  <div className="text-[11px] text-[#78716C] mt-0.5">
                    ROI {pct(liveTotals.roi)} · Profit {fmt(liveTotals.profit)}
                  </div>
                </div>
                <span
                  className={`text-[11px] uppercase tracking-[0.15em] font-medium border rounded-full px-2.5 py-1 ${TONE_CLASSES[baseFeas.tone]}`}
                  data-testid="report-feas-current"
                >
                  {baseFeas.label}
                </span>
              </div>
              <div className="border-t border-black/[0.06] pt-3 flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="fc-label">At offer of {fmt(offer)}</div>
                  <div className="text-[11px] text-[#78716C] mt-0.5">
                    ROI {pct(liveTotals.roiAtOffer)} · Profit {fmt(liveTotals.profitAtOffer)}
                  </div>
                </div>
                <span
                  className={`text-[11px] uppercase tracking-[0.15em] font-medium border rounded-full px-2.5 py-1 ${TONE_CLASSES[offerFeas.tone]}`}
                  data-testid="report-feas-offer"
                >
                  {offerFeas.label}
                </span>
              </div>
            </div>
          )}

          <div className="text-[11px] text-[#A8A29E]">
            Tip: tune your soft costs (contingency, holding) on the Soft Costs tab — they roll into the report's total automatically.
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            data-testid="report-cancel-btn"
            onClick={() => onOpenChange(false)}
            className="h-9 px-3 text-sm"
          >
            Cancel
          </Button>
          <Button
            data-testid="report-generate-btn"
            onClick={persistAndGenerate}
            disabled={busy}
            className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E] h-9 px-3 text-sm"
          >
            <FileDown className="w-4 h-4 mr-1.5" />
            {busy ? "Generating…" : "Generate PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pct(n) {
  if (!isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
}
